import * as XLSX from "xlsx";

// Plantilla de renglones para Orden de Compra y Compra Directa: SKU, CANTIDAD, PRECIO (vacio = costo del articulo)
const normTxt = (t) =>
  String(t == null ? "" : t)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]/g, "");
const COLUMNAS_OC = {
  sku: "sku",
  skuvariante: "sku",
  cantidad: "cantidad",
  piezas: "cantidad",
  precio: "precio",
  preciounitario: "precio",
};
// vacio = null; texto con $ o comas se limpia
const numeroExcel = (v) => {
  if (v === "" || v == null) return null;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/[$,\s]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

// tipo "OC" (Orden de Compra) o "COMPRA" (Compra Directa): mismas columnas, cambian titulo y hoja
export function armarPlantillaOC(tipo = "OC") {
  const esCompra = tipo === "COMPRA";
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([
    ["SKU *", "CANTIDAD *", "PRECIO"],
    esCompra ? ["EJEMPLO-JV-BLA-5M", 500, 12.5] : ["EJEMPLO-M001-JAZZ-140-BLA", 500, 12.5],
    esCompra ? ["EJEMPLO-CS-RF-1M-AZ", 1200, ""] : ["EJEMPLO-M005-RAFAVIN-140-AZU", 1200, ""],
  ]);
  ws["!cols"] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }];
  const wsI = XLSX.utils.aoa_to_sheet([
    [esCompra ? "PLANTILLA DE COMPRA DIRECTA" : "PLANTILLA DE ORDEN DE COMPRA"],
    [""],
    ["1. En la pantalla elige primero el Proveedor (su moneda es la del precio); después sube este archivo."],
    ...(esCompra
      ? [
          ["2. SKU: el SKU de la VARIANTE tal como está en el sistema (la presentación que se compra: 1 m, 5 m, etc.)."],
          ["   Si pones el SKU de un MASTER, entra a su variante; si tiene varias presentaciones (rollos), a la más chica: cantidad y precio por unidad base (p. ej. metros)."],
        ]
      : [
          ["2. SKU: el SKU del MASTER (el producto). Un SKU de variante se rechaza: usa el de su master."],
          ["   Al recibir entra a su variante; si tiene varias presentaciones (rollos, packs), a la más chica: cantidad y precio por unidad base (metros, piezas)."],
        ]),
    ["3. CANTIDAD: piezas a comprar (mayor a 0)."],
    ["4. PRECIO: precio unitario en la moneda del proveedor. Vacío = costo del artículo (USD o MXN según el proveedor)."],
    ["5. Los renglones cuyo SKU empieza con EJEMPLO se ignoran."],
    [`6. Los renglones se agregan a la captura: revísalos y guarda ${esCompra ? "la compra" : "la OC"} como siempre.`],
  ]);
  wsI["!cols"] = [{ wch: 110 }];
  XLSX.utils.book_append_sheet(wb, ws, esCompra ? "COMPRA" : "OC");
  XLSX.utils.book_append_sheet(wb, wsI, "INSTRUCCIONES");
  return wb;
}

// Lee la plantilla y cruza los SKU contra el catalogo. Regresa los renglones listos para la captura
// (sin id) y lo que no se pudo cargar. Solo variantes: el master comparte SKU con su variante
// cuando el producto tiene una sola presentacion, y comprarlo dejaria la mercancia en el master.
// precioDefault(art): precio cuando la celda viene vacia (por default, costo segun la moneda).
// opciones.aceptarMasters: un SKU que solo es de un master se acepta (Compra Directa: el back lo
// mete a su variante). opciones.soloMasters: la OC se captura por master (al recibir entra a su
// variante); un SKU que solo es de una variante se rechaza.
export function leerExcelOC(wb, articulos, moneda, precioDefault, opciones = {}) {
  const nombreHoja = wb.SheetNames.find((n) => ["oc", "compra"].includes(normTxt(n))) || wb.SheetNames[0];
  const ws = wb.Sheets[nombreHoja];
  const inicio = XLSX.utils.decode_range(ws["!ref"] || "A1").s.r;
  const filas = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true, blankrows: true });
  const iHead = filas.findIndex(
    (f) =>
      f.some((c) => COLUMNAS_OC[normTxt(c)] === "sku") &&
      f.some((c) => COLUMNAS_OC[normTxt(c)] === "cantidad")
  );
  if (iHead < 0) return { error: "No encontré las columnas SKU y CANTIDAD (usa la plantilla)." };
  const col = {};
  filas[iHead].forEach((c, j) => {
    const k = COLUMNAS_OC[normTxt(c)];
    if (k && col[k] === undefined) col[k] = j;
  });

  const porSku = new Map();
  articulos.forEach((a) => {
    const k = String(a.SKU || "").trim().toUpperCase();
    if (!k) return;
    if (!porSku.has(k)) porSku.set(k, []);
    porSku.get(k).push(a);
  });
  const candidatos = (k) => (porSku.get(k) || []).filter((a) => a.is_active !== "No");
  const elegir = (k) => {
    if (opciones.soloMasters) return candidatos(k).find((a) => a.modelo === "MASTER") || null;
    const c = candidatos(k).filter((a) => a.modelo !== "MASTER");
    const v = c.find((a) => a.articuloPadre) || c[0] || null;
    if (v || !opciones.aceptarMasters) return v;
    return candidatos(k).find((a) => a.modelo === "MASTER") || null;
  };

  const renglones = [];
  const noEncontrados = [];
  const invalidos = [];
  for (let i = iHead + 1; i < filas.length; i++) {
    const f = filas[i];
    const renglon = inicio + i + 1;
    const sku = String(f[col.sku] == null ? "" : f[col.sku]).trim();
    if (!sku && (f[col.cantidad] === "" || f[col.cantidad] == null)) continue;
    if (/^ejemplo/i.test(sku)) continue;
    const art = elegir(sku.toUpperCase());
    if (!art) {
      if (candidatos(sku.toUpperCase()).length) {
        invalidos.push(
          `Renglón ${renglon}: ${sku} — ${opciones.soloMasters ? "es una variante, usa el SKU de su master" : "es un master, usa el SKU de la variante"}`
        );
      } else {
        noEncontrados.push(`Renglón ${renglon}: ${sku || "(sin SKU)"}`);
      }
      continue;
    }
    const cantidad = numeroExcel(f[col.cantidad]);
    if (!(cantidad > 0)) {
      invalidos.push(`Renglón ${renglon}: ${sku} — cantidad "${f[col.cantidad]}"`);
      continue;
    }
    let precio = col.precio !== undefined ? numeroExcel(f[col.precio]) : null;
    if (precio === null)
      precio = (precioDefault ? precioDefault(art) : moneda === "USD" ? art.costoUSD : art.costoMX) || 0;
    if (Number.isNaN(precio) || precio < 0) {
      invalidos.push(`Renglón ${renglon}: ${sku} — precio "${f[col.precio]}"`);
      continue;
    }
    renglones.push({
      articulos: art._id,
      articulosNombre: { ...art, junta: (art.SKU || "") + (art.nombre || "") },
      cantidad,
      precio,
      total: parseFloat((cantidad * precio).toFixed(2)),
    });
  }
  return { renglones, noEncontrados, invalidos };
}
